import React, { useMemo } from 'react';
import Divider from '../../Divider';
import NumField from '../../NumField';
import { CapsuleTabContentBody, CapsuleTabContentHeader } from '../CapsuleComponents';
import { addDays, format } from 'date-fns/fp';
import { IFormState } from '../../../semshared/utils/lister';
import { set } from 'lodash';
import { LinkButton } from '../../LinkButton';
import { useTranslation } from 'react-i18next';

interface RoomSectionProps {
  formState: IFormState
  requestFormStateUpdate: (newState: IFormState) => void;
  setSimpleMode: (simpleMode: boolean) => void;
  simpleMode: boolean;
  setDrawerShow: (s: string) => void;
  setPopupShow: (s: string | null) => void;
}

/**
 * Overnight room editor for single and double occupancy values.
 * @param props - Current state and update handlers for simple/advanced room flows.
 * @returns Room section UI for popup or drawer mode.
 */
const RoomsSection = ({ formState, requestFormStateUpdate, setSimpleMode, simpleMode, setDrawerShow, setPopupShow }: RoomSectionProps) => {
  const { t } = useTranslation();

  const accommodationWarnings = useMemo(() => {
    return formState.days.map((day, index) => {
      const totalParticipants = day.totalGuests || 0;
      const singleRooms = day.overnightRoomsEZ || 0;
      const doubleRooms = day.overnightRoomsDZ || 0;

      // People in double rooms count x2
      const totalAccommodation = singleRooms + (doubleRooms * 2);

      return {
        dayIndex: index,
        totalParticipants,
        totalAccommodation,
        hasWarning: totalAccommodation > totalParticipants,
      };
    });
  }, [formState.days]);

  // Check prevday warning
  const prevdayWarning = useMemo(() => {
    // For prevday, we need to check against the first day's participants
    const totalParticipants = formState.days[0]?.totalGuests || 0;
    const singleRooms = formState.prevdayRoomsEZ || 0;
    const doubleRooms = formState.prevdayRoomsDZ || 0;
    const totalAccommodation = singleRooms + (doubleRooms * 2);

    return {
      totalParticipants,
      totalAccommodation,
      hasWarning: totalAccommodation > totalParticipants,
    };
  }, [formState.prevdayRoomsEZ, formState.prevdayRoomsDZ, formState.days]);

  const simple = () => {
    return <>
      <CapsuleTabContentHeader>{t('widget:headers.pleaseEnterOvernights')}</CapsuleTabContentHeader>
      <CapsuleTabContentBody>
        {!formState.start && <div></div>}
        <div
          style={{
            display: 'flex',
            fontFamily: 'Roboto, sans-serif',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              minWidth: 50,
              paddingRight: 40,
              textAlign: 'left',
            }}
          >
            <>
              <strong>{t('widget:room.arrivalPrevDay')}</strong>
              <div>{format('dd.MM.yyyy', addDays(-1, formState.start || new Date()))}</div>
            </>
          </div>
          <div
            style={{
              paddingRight: 0,
              alignSelf: 'center',
            }}
          >
            <NumField
              value={formState.prevdayRoomsEZ}
              onChange={value =>
                requestFormStateUpdate({
                  ...formState,
                  prevdayRoomsEZ: value,
                })
              }
            />
          </div>
        </div>
        {formState.start && formState.days.length > 1 && <>
          <Divider />
          <div
            style={{
              display: 'flex',
              fontFamily: 'Roboto, sans-serif',
              justifyContent: 'space-between',
            }}
          >
            <div
              style={{
                minWidth: 50,
                paddingRight: 40,
                textAlign: 'left',
              }}
            >
              <>
                <strong>{t('widget:roomLabels.seminarDays')}</strong>
                <div>{format('dd.MM', addDays(0, formState.start || new Date()))} - {format('dd.MM.yy', addDays(formState.days.length - 1, formState.start || new Date()))}</div>
              </>
            </div>
            <div
              style={{
                paddingRight: 0,
                alignSelf: 'center',
              }}
            >
              <React.Fragment>
                <NumField
                  value={formState.days[0].overnightRoomsEZ}
                  onChange={value => {
                    requestFormStateUpdate({
                      ...formState,
                      days: formState.days.map((day, i) => {
                        // Seminar-day overnight value applies to all seminar nights except extension night.
                        if (formState.days.length - 1 !== i) {
                          return {
                            ...day,
                            overnightRoomsEZ: value,
                          };
                        } else {
                          return day;
                        }
                      }),
                    });
                  }}
                />
              </React.Fragment>
            </div>
          </div>
        </>}
        <Divider />
        {formState.start &&
          <div
            style={{
              display: 'flex',
              fontFamily: 'Roboto, sans-serif',
              justifyContent: 'space-between',
            }}
          >
            <div
              style={{
                minWidth: 50,
                paddingRight: 40,
                textAlign: 'left',
              }}
            >
              <>
                <strong>{t('widget:roomLabels.additionalNight')}</strong>
                <div>{t('widget:roomLabels.on')} {format('dd.MM.yy', addDays(formState.days.length, formState.start || new Date()))}</div>
              </>
            </div>
            <div
              style={{
                paddingRight: 0,
                alignSelf: 'center',
              }}
            >
              <React.Fragment>
                <NumField
                  value={formState.days[formState.days.length - 1].overnightRoomsEZ}
                  onChange={value => {
                    requestFormStateUpdate({
                      ...formState,
                      days: formState.days.map((day, i) => {
                        if (formState.days.length - 1 === i) {
                          return {
                            ...day,
                            overnightRoomsEZ: value,
                          };
                        } else {
                          return day;
                        }
                      }
                      ),
                    });
                  }}
                />
              </React.Fragment>
            </div>
          </div>}
        <Divider />
        <LinkButton onClick={() => {
          setDrawerShow('rooms')
          setPopupShow(null)
        }}>⇾ Details</LinkButton>
      </CapsuleTabContentBody>
    </>
  }

  const advanced = () => {
    return <>
      <CapsuleTabContentBody>
        <div
          style={{
            display: 'flex',
            fontFamily: 'Roboto, sans-serif',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              minWidth: 50,
              paddingRight: 40,
              textAlign: 'left',
            }}
          >

          </div>
          <div
            style={{
              paddingRight: 0,
              alignSelf: 'center',
              display: 'flex',
              gap: 20,
              fontWeight: 'bold',
            }}
          >
            <div style={{ width: 120, textAlign: 'center' }}>{t('widget:room.personsInSingle')}</div>
            <div style={{ width: 120, textAlign: 'center' }}>{t('widget:room.personsInDouble')}</div>
          </div>
        </div>
        <Divider />
        <div
          style={{
            display: 'flex',
            fontFamily: 'Roboto, sans-serif',
            justifyContent: 'space-between',
          }}
        >
          <div
            style={{
              minWidth: 50,
              paddingRight: 40,
              textAlign: 'left',
            }}
          >
            <>
              <strong>{t('widget:room.arrivalPrevDay')}</strong>
              <div>{format('dd.MM.yyyy', addDays(-1, formState.start || new Date()))}</div>
            </>
          </div>
          <div
            style={{
              paddingRight: 0,
              alignSelf: 'center',
              display: 'flex',
              gap: 5,
            }}
          >
            <NumField
              value={formState.prevdayRoomsEZ}
              onChange={value =>
                requestFormStateUpdate({
                  ...formState,
                  prevdayRoomsEZ: value,
                })
              }
            />
            <NumField
              value={formState.prevdayRoomsDZ}
              onChange={value => {
                // Double rooms are stored as participant count and must remain even.
                const newValue = value % 2 === 0 ? value : (formState.prevdayRoomsDZ > value ? value - 1 : value + 1);
                requestFormStateUpdate({
                  ...formState,
                  prevdayRoomsDZ: newValue,
                })
              }}
            />
          </div>
        </div>
        {prevdayWarning.hasWarning && (
          <div style={{
            color: '#ff0000',
            fontSize: '12px',
            fontWeight: 'bold',
            marginTop: '8px',
            marginBottom: '8px',
            textAlign: 'right',
            paddingRight: 40,
          }}>
            {prevdayWarning.totalAccommodation} {t('widget:room.peopleOf')} {prevdayWarning.totalParticipants} {t('widget:room.assigned')}
          </div>
        )}

        <Divider />
        {formState.start &&
          [...Array(formState.days.length)].map((_, index) => {
            const warning = accommodationWarnings[index];
            return (
              <React.Fragment key={index}>
                <div
                  style={{
                    display: 'flex',
                    fontFamily: 'Roboto, sans-serif',
                    justifyContent: 'space-between',
                  }}
                >
                  <div
                    style={{
                      minWidth: 50,
                      paddingRight: 40,
                      textAlign: 'left',
                    }}
                  >
                    <>
                      <strong>{formState.days.length - 1 === index ? t('widget:room.extensionNight') : `${t('widget:room.day')} ${index + 1}`}</strong>
                      <div>{format('dd.MM.yyyy', addDays(index, formState.start || new Date()))}</div>
                    </>
                  </div>
                  <div
                    style={{
                      paddingRight: 0,
                      alignSelf: 'center',
                      display: 'flex',
                      gap: 5,
                    }}
                  >
                    <NumField
                      value={formState.days[index].overnightRoomsEZ}
                      onChange={value => {
                        requestFormStateUpdate({
                          ...formState,
                          days: formState.days.map((day, i) => {
                            if (index === i) {
                              return {
                                ...day,
                                overnightRoomsEZ: value,
                              };
                            } else {
                              return day;
                            }
                          }),
                        });
                      }}
                    />
                    <NumField
                      value={formState.days[index].overnightRoomsDZ}
                      onChange={value => {
                        const newValue = value % 2 === 0 ? value : (formState.days[index].overnightRoomsDZ > value ? value - 1 : value + 1);
                        requestFormStateUpdate({
                          ...formState,
                          days: formState.days.map((day, i) => {
                            if (index === i) {
                              return {
                                ...day,
                                overnightRoomsDZ: newValue,
                              };
                            } else {
                              return day;
                            }
                          }),
                        });
                      }}
                    />
                  </div>
                </div>
                {warning.hasWarning && (
                  <div style={{
                    color: '#ff0000',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    marginTop: '4px',
                    marginBottom: '8px',
                    textAlign: 'right',
                    paddingRight: 40,
                  }}>
                    {warning.totalAccommodation} {t('widget:room.peopleOf')} {warning.totalParticipants} {t('widget:room.assigned')}
                  </div>
                )}
                <Divider />
              </React.Fragment>
            );
          })}
      </CapsuleTabContentBody>
    </>
  }

  return <>
    {simpleMode ? simple() : advanced()}
  </>
}

export default RoomsSection;
